import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import {
  Alerts,
  Button,
  Col,
  Container,
  Field,
  Header,
  Heading,
  PageContent,
  Row,
  Select,
  Spinner,
} from 'components';
import { EmptyNote, FormRow, NarrowInput } from '../GlobalEvents.styled';

export const LIST_PATH = '/ipma/global-events';

const emptyEntry = { id: null, year: null, event: null, youngCrews: [] };

/**
 * The screen a global events participation entry is written on, whether it is
 * being added or changed.
 *
 * It is a screen of its own rather than a form folded out under the table, the
 * way every other add and edit in the app works, so the reader keeps the whole
 * width for the Young Crews they are picking and the table stays a table.
 */
const GlobalEventForm = ({ mode, entry, options, errors, loading, onSave, clearErrors }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { eventOptions, youngCrewOptions, yearOptions } = options;
  const isEdit = mode === 'edit';

  const [values, setValues] = useState(emptyEntry);

  useEffect(() => {
    clearErrors();
  }, []);

  useEffect(() => {
    if (!entry) return;

    setValues({
      id: entry.id,
      year: entry.year,
      event: entry.event,
      youngCrews: (entry.youngCrews || []).map((crew) => crew.value),
    });
  }, [entry]);

  const breadcrumbs = [
    {
      title: 'navigation.breadcrumbs.ipma.globalEvents',
      url: LIST_PATH,
    },
    {
      title: isEdit
        ? 'navigation.breadcrumbs.ipma.globalEventsEdit'
        : 'navigation.breadcrumbs.ipma.globalEventsAdd',
      url: '',
    },
  ];

  const update = (field) => (value) => setValues((previous) => ({ ...previous, [field]: value }));

  const submit = () => onSave(values, history);

  // An entry that is being changed has to be read before it can be written to.
  const waitingForEntry = isEdit && !entry;

  return (
    <>
      <Header
        breadcrumbs={breadcrumbs}
        title={
          isEdit ? t('page.ipma.globalEvents.editTitle') : t('page.ipma.globalEvents.addTitle')
        }
      />
      <PageContent>
        <Container>
          <Alerts type="ipma" />

          {loading && waitingForEntry ? (
            <Spinner />
          ) : (
            <Col>
              <Margin bottom="24">
                <Heading size="h4">{t('page.ipma.globalEvents.formSection')}</Heading>
                <div style={{ marginTop: '10px' }}>{t('page.ipma.globalEvents.formHelp')}</div>
              </Margin>

              <FormRow>
                <NarrowInput>
                  <Field
                    id="ge-year"
                    label={t('page.ipma.globalEvents.year')}
                    required
                    error={errors.year}
                  >
                    <Select
                      id="ge-year"
                      type="text"
                      placeholder={t('page.ipma.globalEvents.yearPlaceholder')}
                      value={yearOptions.find((option) => option.value === values.year) || ''}
                      options={yearOptions}
                      onChange={(option) => update('year')(option ? option.value : null)}
                    />
                  </Field>
                </NarrowInput>
                <NarrowInput>
                  <Field
                    id="ge-event"
                    label={t('page.ipma.globalEvents.event')}
                    required
                    error={errors.event}
                  >
                    <Select
                      id="ge-event"
                      type="text"
                      placeholder={t('page.ipma.globalEvents.eventPlaceholder')}
                      value={eventOptions.find((option) => option.value === values.event) || ''}
                      options={eventOptions}
                      onChange={(option) => update('event')(option ? option.value : null)}
                    />
                  </Field>
                </NarrowInput>
              </FormRow>

              <Margin top="24" bottom="32">
                <Field
                  id="ge-crews"
                  label={t('page.ipma.globalEvents.youngCrews')}
                  description={t('page.ipma.globalEvents.youngCrewsHelp')}
                  required
                  error={errors.youngCrews}
                >
                  {youngCrewOptions.length ? (
                    <Select
                      id="ge-crews"
                      type="text"
                      isMulti
                      closeMenuOnSelect={false}
                      placeholder={t('page.ipma.globalEvents.youngCrewsPlaceholder')}
                      value={youngCrewOptions.filter((option) =>
                        values.youngCrews.includes(option.value),
                      )}
                      options={youngCrewOptions}
                      onChange={(selected) =>
                        update('youngCrews')((selected || []).map((option) => option.value))
                      }
                    />
                  ) : (
                    <EmptyNote>{t('page.ipma.globalEvents.youngCrewsEmpty')}</EmptyNote>
                  )}
                </Field>
              </Margin>

              <Row>
                <Col size={{ lg: 1 / 4 }}>
                  <Button type="button" onClick={submit}>
                    {t('global.saveButton')}
                  </Button>
                </Col>
                <Col size={{ lg: 1 / 4 }}>
                  <Button type="button" secondary onClick={() => history.push(LIST_PATH)}>
                    {t('global.cancelButton')}
                  </Button>
                </Col>
              </Row>
            </Col>
          )}
        </Container>
      </PageContent>
    </>
  );
};

GlobalEventForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  entry: PropTypes.shape({
    id: PropTypes.number,
    year: PropTypes.number,
    event: PropTypes.string,
    youngCrews: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  options: PropTypes.shape({
    eventOptions: PropTypes.arrayOf(PropTypes.shape({})),
    youngCrewOptions: PropTypes.arrayOf(PropTypes.shape({})),
    yearOptions: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  loading: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
};

GlobalEventForm.defaultProps = {
  entry: null,
  errors: {},
  loading: false,
};

export default GlobalEventForm;
